import type { Environment } from 'react-relay';
import type { SearchTemplate } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type PropTypes,
	type PropsResolver,
	PickerPropTypes,
} from '../../../../../common/types.tsx';
import type { JiraProjectOptionsType, JQLEditorUIProps } from '../../../../../common/ui/types.tsx';
import { isPickerPropType } from '../../../../../common/utils.tsx';
import { getValidOperandDisplayValues } from '../../../../../utils/get-valid-operands.tsx';
import { getCommonProps } from './common/getCommonProps.tsx';
import { getGroupProps } from './group/getGroupProps.tsx';
import { getIssueTypeProps } from './issue-type/getIssueTypeProps.tsx';
import { getPeopleProps } from './people/getPeopleProps.tsx';
import { getProjectProps } from './project/getProjectProps.tsx';
import { getSprintProps } from './sprint/getSprintProps.tsx';

export type Params = {
	fieldId: string;
	propType: PropTypes;
	searchTemplate: SearchTemplate;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	projectOptions?: JiraProjectOptionsType;
	viewContext: JQLEditorUIProps['viewContext'];
	scope: JQLEditorUIProps['scope'];
	suggestedUsersCount?: number;
};

export const getPickerProps = ({
	fieldId,
	propType,
	searchTemplate,
	formatMessage,
	jql,
	environment,
	cloudId,
	projectOptions,
	viewContext,
	scope,
	suggestedUsersCount,
}: Params): PropsResolver => {
	const commonProps = {
		fieldId,
		searchTemplate,
		propType,
		formatMessage,
		jql,
		environment,
		cloudId,
		operandDisplayValues: getValidOperandDisplayValues(propType),
		viewContext,
		scope,
		suggestedUsersCount,
	};

	if (!isPickerPropType(propType)) {
		throw new Error(`Unsupported PickerPropType: ${propType}`);
	}

	switch (propType) {
		case PickerPropTypes.IssueType:
			return getIssueTypeProps(commonProps);
		case PickerPropTypes.User:
			return getPeopleProps(commonProps);
		case PickerPropTypes.Sprint:
			return getSprintProps(commonProps);
		case PickerPropTypes.Common:
			return getCommonProps(commonProps);
		case PickerPropTypes.CommonWithEmpty:
		case PickerPropTypes.Version:
			return getCommonProps({
				...commonProps,
				showOperandDisplayValuesInFullList: true,
			});
		case PickerPropTypes.Project:
			return getProjectProps({ ...commonProps, projectOptions });
		case PickerPropTypes.Group:
			return getGroupProps(commonProps);
		default:
			throw new Error(`Unsupported PickerPropType: ${propType}`);
	}
};
